import logger from '../utils/logger';

class ProfileStrengthTaskService {
    static generateTasksFromProfileData(profileData) {
        const tasks = [];
        const { business, profileScore, gaps, competitors, velocity } = profileData;

        // AI-powered intelligent task generation based on detailed business metrics
        logger.log('[AI Task Generation] Analyzing business data for intelligent recommendations...');

        // Try to use TaskManagementDataService for comprehensive analysis if available
        try {
            const TaskManagementDataService = require('./TaskManagementDataService').default;
            if (TaskManagementDataService && TaskManagementDataService.generateAITaskRecommendations) {
                // Convert profileData to comprehensiveData format for new service
                const comprehensiveData = {
                    businessProfile: business,
                    gaps: gaps || {},
                    velocityData: velocity || { velocityScore: 0, reviewsLast30Days: 0 },
                    gmbFeedData: { gmbFeedScore: 0, postsLast90Days: 0 },
                    competitors: competitors || [],
                    scoreBreakdown: [] // Add empty scoreBreakdown to prevent errors
                };

                // Call the async function and await it
                const aiTasksPromise = TaskManagementDataService.generateAITaskRecommendations(comprehensiveData);

                // Check if it's a promise
                if (aiTasksPromise && typeof aiTasksPromise.then === 'function') {
                    // It's async, we need to handle this differently
                    // For now, use fallback since we can't await here
                    throw new Error('Async task generation not supported in this context');
                }

                const aiTasks = aiTasksPromise;
                if (Array.isArray(aiTasks) && aiTasks.length > 0) {
                    tasks.push(...aiTasks);
                    logger.log(`[AI Task Generation] Generated ${aiTasks.length} tasks using TaskManagementDataService`);
                } else {
                    // Fallback to original logic
                    const fallbackTasks = this.generateAIRecommendations(business, gaps, velocity, competitors);
                    tasks.push(...fallbackTasks);
                }
            } else {
                // Fallback to original logic
                const aiTasks = this.generateAIRecommendations(business, gaps, velocity, competitors);
                tasks.push(...aiTasks);
            }
        } catch (error) {
            logger.warn('[AI Task Generation] TaskManagementDataService not available, using fallback:', error);
            // Analyze specific scoring areas and provide targeted recommendations
            const aiTasks = this.generateAIRecommendations(business, gaps, velocity, competitors);
            if (Array.isArray(aiTasks)) {
                tasks.push(...aiTasks);
            }
        }

        // Rules-based task generation from profile gaps (only for items not covered by AI)
        if (gaps) {
            // Skip website task as it's already handled by AI recommendations

            // Skip photos task as it's already handled by AI recommendations with better context

            // Business hours missing
            if (gaps.hours) {
                tasks.push({
                    id: `hours-${Date.now()}`,
                    title: 'Complete Business Hours',
                    description: 'Add complete business hours for all days',
                    priority: 'High',
                    category: 'Profile Completion',
                    source: 'profile-strength',
                    estimatedTime: '15 minutes'
                });
            }

            // Attributes missing
            if (gaps.attributes) {
                tasks.push({
                    id: `attributes-${Date.now()}`,
                    title: 'Add Business Attributes',
                    description: 'Complete business attributes (payment methods, accessibility, etc.)',
                    priority: 'Medium',
                    category: 'Profile Completion',
                    source: 'profile-strength',
                    estimatedTime: '20 minutes'
                });
            }

            // Description missing or incomplete
            if (gaps.description) {
                tasks.push({
                    id: `description-${Date.now()}`,
                    title: 'Improve Business Description',
                    description: 'Write a compelling business description with keywords',
                    priority: 'Medium',
                    category: 'Content',
                    source: 'profile-strength',
                    estimatedTime: '25 minutes'
                });
            }
        }

        // Generate tasks based on profile score
        if (profileScore && profileScore < 80) {
            tasks.push({
                id: `optimization-${Date.now()}`,
                title: 'Profile Optimization Review',
                description: `Your profile score is ${profileScore}%. Review and improve incomplete sections.`,
                priority: 'High',
                category: 'Optimization',
                source: 'profile-strength',
                estimatedTime: '60 minutes'
            });
        }

        // Competitor-based tasks
        if (competitors && competitors.length > 0) {
            const topCompetitor = competitors[0];
            if (topCompetitor.reviewCount > (business.reviewCount || 0) * 2) {
                tasks.push({
                    id: `reviews-${Date.now()}`,
                    title: 'Increase Review Collection',
                    description: `Competitor has ${topCompetitor.reviewCount} reviews vs your ${business.reviewCount || 0}. Implement review collection strategy.`,
                    priority: 'High',
                    category: 'Reputation',
                    source: 'profile-strength',
                    estimatedTime: '90 minutes'
                });
            }

            if (topCompetitor.rating > (business.rating || 0) + 0.5) {
                tasks.push({
                    id: `rating-${Date.now()}`,
                    title: 'Improve Customer Satisfaction',
                    description: `Competitor has ${topCompetitor.rating} rating vs your ${business.rating || 0}. Focus on service quality improvement.`,
                    priority: 'Critical',
                    category: 'Reputation',
                    source: 'profile-strength',
                    estimatedTime: '120 minutes'
                });
            }
        }

        // Deduplicate tasks based on title to prevent duplicates
        const uniqueTasks = this.deduplicateTasks(tasks);

        logger.log(`[AI Task Generation] Generated ${uniqueTasks.length} unique tasks (${tasks.length - uniqueTasks.length} duplicates removed)`);

        return uniqueTasks;
    }

    static saveProfileStrengthData(profileData) {
        const timestamp = Date.now();
        const businessId = profileData.business?.id || 'default';
        const storageKey = `profile_strength_${businessId}_${timestamp}`;

        const summary = {
            businessId,
            timestamp,
            business: profileData.business,
            profileScore: profileData.profileScore,
            gaps: profileData.gaps,
            competitors: profileData.competitors?.slice(0, 5), // Limit stored competitor data
            runId: this.generateRunId(profileData)
        };

        localStorage.setItem(storageKey, JSON.stringify(summary));
        localStorage.setItem('latest_profile_strength', storageKey);

        return summary;
    }

    static getLatestProfileStrengthData() {
        const latestKey = localStorage.getItem('latest_profile_strength');
        if (latestKey) {
            const data = localStorage.getItem(latestKey);
            return data ? JSON.parse(data) : null;
        }
        return null;
    }

    static generateRunId(profileData) {
        // Generate deterministic runId to avoid duplicate task generation
        const str = JSON.stringify({
            businessId: profileData.business?.id,
            profileScore: profileData.profileScore,
            gaps: profileData.gaps
        });
        return btoa(str).substring(0, 16);
    }

    /**
     * AI-powered task generation based on detailed business metrics
     */
    static generateAIRecommendations(business, gaps, velocity, competitors) {
        const aiTasks = [];
        const businessType = this.detectBusinessType(business);

        logger.log(`[AI Task Generation] Detected business type: ${businessType}`);

        // Comprehensive Business Profile Analysis
        const profileAnalysis = this.performComprehensiveProfileAnalysis(business, businessType);
        logger.log('[AI Analysis] Profile analysis results:', profileAnalysis);

        // Generate tasks based on comprehensive analysis
        const analysisBasedTasks = this.generateTasksFromProfileAnalysis(profileAnalysis, businessType);
        aiTasks.push(...analysisBasedTasks);

        // Analyze velocity score (0 points = no recent reviews)
        if (velocity && velocity.velocityScore === 0) {
            aiTasks.push({
                id: `velocity-improvement-${Date.now()}`,
                title: '🚀 Boost Review Velocity',
                description: `Your review velocity is 0 (no reviews in last 30 days). ${this.getVelocityRecommendation(businessType)}`,
                priority: 'Critical',
                category: 'Reputation Management',
                source: 'ai-analysis',
                estimatedTime: '45 minutes',
                aiInsight: 'Zero review velocity indicates customers aren\'t being asked for feedback'
            });
        }

        // Analyze response rate (0 = not responding to reviews)
        const responseRate = business.performance?.responseRate || 0;
        if (responseRate === 0 && business.reviewCount > 0) {
            aiTasks.push({
                id: `response-rate-${Date.now()}`,
                title: '💬 Start Responding to Reviews',
                description: `You have ${business.reviewCount} reviews but 0% response rate. ${this.getResponseRateRecommendation(businessType)}`,
                priority: 'High',
                category: 'Customer Engagement',
                source: 'ai-analysis',
                estimatedTime: '30 minutes',
                aiInsight: 'Responding to reviews shows you value customer feedback and improves local SEO'
            });
        }

        // Analyze additional categories (0 points)
        if (gaps.categories) {
            aiTasks.push({
                id: `categories-expansion-${Date.now()}`,
                title: '🏷️ Add Additional Business Categories',
                description: `Add relevant secondary categories to improve discoverability. ${this.getCategoryRecommendation(businessType)}`,
                priority: 'Medium',
                category: 'Profile Optimization',
                source: 'ai-analysis',
                estimatedTime: '20 minutes',
                aiInsight: 'Additional categories help customers find you for related services'
            });
        }

        // Analyze website presence
        if (gaps.website) {
            aiTasks.push({
                id: `website-critical-${Date.now()}`,
                title: '🌐 Add Business Website (Critical)',
                description: `${this.getWebsiteRecommendation(businessType, competitors)}`,
                priority: 'Critical',
                category: 'Digital Presence',
                source: 'ai-analysis',
                estimatedTime: '60 minutes',
                aiInsight: 'Websites increase credibility and provide more conversion opportunities'
            });
        }

        // Analyze booking capabilities
        if (this.shouldHaveBooking(businessType) && !business.attributes?.some(attr => attr.includes('appointment'))) {
            aiTasks.push({
                id: `booking-system-${Date.now()}`,
                title: '📅 Enable Online Booking',
                description: `${businessType} businesses benefit greatly from online booking. Set up appointment scheduling to capture more customers.`,
                priority: 'High',
                category: 'Customer Experience',
                source: 'ai-analysis',
                estimatedTime: '90 minutes',
                aiInsight: 'Online booking reduces friction and increases conversion rates'
            });
        }

        // Analyze photo strategy based on business type
        if (gaps.photos) {
            const photoStrategy = this.getPhotoStrategy(businessType, business.photos?.length || 0);
            aiTasks.push({
                id: `photo-strategy-${Date.now()}`,
                title: photoStrategy.title,
                description: photoStrategy.description,
                priority: 'High',
                category: 'Visual Marketing',
                source: 'ai-analysis',
                estimatedTime: photoStrategy.time,
                aiInsight: photoStrategy.insight
            });
        }

        // Competitive analysis recommendations
        if (competitors && competitors.length > 0) {
            const competitiveInsights = this.analyzeCompetitiveGaps(business, competitors);
            aiTasks.push(...competitiveInsights);
        }

        return aiTasks;
    }

    static detectBusinessType(business) {
        const category = (business.category || '').toLowerCase();
        const name = (business.name || '').toLowerCase();

        if (category.includes('restaurant') || category.includes('food') || name.includes('restaurant')) return 'Restaurant';
        if (category.includes('medical') || category.includes('doctor') || category.includes('health')) return 'Healthcare';
        if (category.includes('salon') || category.includes('beauty') || category.includes('spa')) return 'Beauty/Wellness';
        if (category.includes('auto') || category.includes('car') || name.includes('auto')) return 'Automotive';
        if (category.includes('real estate') || category.includes('realtor')) return 'Real Estate';
        if (category.includes('legal') || category.includes('law') || category.includes('attorney')) return 'Legal';
        if (category.includes('retail') || category.includes('store') || category.includes('shop')) return 'Retail';

        return 'Service Business';
    }

    static getVelocityRecommendation(businessType) {
        const strategies = {
            'Restaurant': 'Ask customers for reviews after their meal. Consider table tents or receipt reminders.',
            'Healthcare': 'Send follow-up emails after appointments asking for feedback on their experience.',
            'Beauty/Wellness': 'Ask satisfied clients for reviews while they\'re still in the chair feeling great.',
            'Automotive': 'Follow up after service completion with a review request via text or email.',
            'Real Estate': 'Request reviews from recent buyers/sellers after successful transactions.',
            'Legal': 'Ask satisfied clients for reviews after case resolution (following ethical guidelines).',
            'Retail': 'Include review requests in purchase confirmations and follow-up emails.',
            'Service Business': 'Follow up with customers 2-3 days after service completion.'
        };

        return strategies[businessType] || strategies['Service Business'];
    }

    static getResponseRateRecommendation(businessType) {
        return 'Respond to all reviews within 24-48 hours. Thank positive reviewers and address concerns professionally in negative reviews.';
    }

    static getCategoryRecommendation(businessType) {
        const suggestions = {
            'Restaurant': 'Consider adding cuisine type, dining style (casual/fine dining), or service options (delivery, takeout).',
            'Healthcare': 'Add specialties, accepted insurance types, or specific services offered.',
            'Beauty/Wellness': 'Include specific services like hair styling, nail care, massage therapy, etc.',
            'Automotive': 'Add services like oil change, tire service, brake repair, or car wash.',
            'Real Estate': 'Specify residential/commercial, buyer/seller agent, or property management.',
            'Legal': 'Add practice areas like family law, criminal defense, personal injury, etc.',
            'Retail': 'Include product categories, brands carried, or shopping experience types.',
            'Service Business': 'Add specific service types, service areas, or specializations.'
        };

        return suggestions[businessType] || suggestions['Service Business'];
    }

    static getWebsiteRecommendation(businessType, competitors) {
        let competitorInsight = '';
        if (competitors && competitors.length > 0) {
            const competitorsWithWebsites = competitors.filter(c => c.website).length;
            if (competitorsWithWebsites > 0) {
                competitorInsight = ` ${competitorsWithWebsites} of your top competitors have websites, putting you at a disadvantage.`;
            }
        }

        return `A professional website is essential for ${businessType.toLowerCase()} businesses to showcase services, build credibility, and capture leads.${competitorInsight}`;
    }

    static shouldHaveBooking(businessType) {
        return ['Healthcare', 'Beauty/Wellness', 'Automotive', 'Legal', 'Service Business'].includes(businessType);
    }

    static getPhotoStrategy(businessType, currentCount) {
        const strategies = {
            'Restaurant': {
                title: '📸 Upload Food & Ambiance Photos',
                description: `Upload high-quality photos of your best dishes, restaurant interior, and happy customers. Target: 15-20 photos.`,
                time: '60 minutes',
                insight: 'Food photos directly influence dining decisions and increase foot traffic'
            },
            'Healthcare': {
                title: '📸 Add Professional Facility Photos',
                description: `Show your clean, professional facility, waiting areas, and equipment. Avoid patient photos for privacy.`,
                time: '45 minutes',
                insight: 'Professional facility photos build trust and reduce patient anxiety'
            },
            'Beauty/Wellness': {
                title: '📸 Showcase Your Work & Space',
                description: `Upload before/after photos (with permission), your workspace, and products used.`,
                time: '50 minutes',
                insight: 'Visual proof of your work quality is the best marketing for beauty services'
            },
            'Automotive': {
                title: '📸 Show Your Shop & Services',
                description: `Upload photos of your facility, equipment, team at work, and completed repairs.`,
                time: '40 minutes',
                insight: 'Showing your professional setup builds trust for expensive automotive services'
            },
            'Retail': {
                title: '📸 Display Products & Store Layout',
                description: `Show your product range, store layout, seasonal displays, and shopping experience.`,
                time: '45 minutes',
                insight: 'Product photos help customers know what to expect before visiting'
            }
        };

        return strategies[businessType] || {
            title: '📸 Add Professional Business Photos',
            description: `Upload photos of your workspace, team, services, and results. Target: 10-15 high-quality photos.`,
            time: '45 minutes',
            insight: 'Professional photos build credibility and help customers choose your business'
        };
    }

    static analyzeCompetitiveGaps(business, competitors) {
        const competitiveTasks = [];

        // Recommend aligning with the most common competitor category
        const competitorCategories = competitors
            .flatMap(comp => Array.isArray(comp.types) ? comp.types : comp.types ? [comp.types] : [])
            .filter(Boolean)
            .map(category => category.toString().trim())
            .filter(category => category.length > 0);

        if (competitorCategories.length > 0) {
            const categoryFrequency = competitorCategories.reduce((freq, category) => {
                const key = category.toLowerCase();
                if (!freq[key]) {
                    freq[key] = { count: 0, original: category };
                }
                freq[key].count += 1;
                return freq;
            }, {});

            const mostFrequentCategory = Object.values(categoryFrequency).reduce((top, current) => {
                return !top || current.count > top.count ? current : top;
            }, null)?.original;

            const userCategory = (business.category ||
                business.categories?.primaryCategory?.displayName ||
                business.categories?.[0]?.displayName ||
                '').trim();

            if (mostFrequentCategory &&
                userCategory &&
                mostFrequentCategory.toLowerCase() !== userCategory.toLowerCase()) {
                competitiveTasks.push({
                    id: `category-alignment-${Date.now()}`,
                    title: 'Update Business Category (Critical)',
                    description: `Your top competitors are using the category '${mostFrequentCategory}', while you are using '${userCategory}'. Changing your category to match competitors can significantly improve your local ranking.`,
                    priority: 'Critical',
                    category: 'Profile Optimization',
                    source: 'competitive-analysis'
                });
            }
        }

        // Analyze average competitor ratings
        const avgCompetitorRating = competitors.reduce((sum, comp) => sum + (comp.rating || 0), 0) / competitors.length;
        if (business.rating < avgCompetitorRating - 0.3) {
            competitiveTasks.push({
                id: `rating-improvement-${Date.now()}`,
                title: '⭐ Improve Customer Satisfaction',
                description: `Your rating (${business.rating}) is below competitor average (${avgCompetitorRating.toFixed(1)}). Focus on service quality and proactively address customer concerns.`,
                priority: 'Critical',
                category: 'Reputation Management',
                source: 'competitive-analysis',
                estimatedTime: '120 minutes',
                aiInsight: 'Higher ratings significantly impact local search rankings and customer choice'
            });
        }

        // Analyze review count gaps
        const avgCompetitorReviews = competitors.reduce((sum, comp) => sum + (comp.reviewCount || 0), 0) / competitors.length;
        if (business.reviewCount < avgCompetitorReviews * 0.5) {
            competitiveTasks.push({
                id: `review-volume-${Date.now()}`,
                title: '📈 Increase Review Volume',
                description: `You have ${business.reviewCount} reviews vs competitor average of ${Math.round(avgCompetitorReviews)}. Implement systematic review collection.`,
                priority: 'High',
                category: 'Reputation Management',
                source: 'competitive-analysis',
                estimatedTime: '90 minutes',
                aiInsight: 'More reviews improve local search visibility and customer trust'
            });
        }

        return competitiveTasks;
    }

    static createWebsiteTask(business, competitors) {
        const businessType = this.detectBusinessType(business);
        return {
            id: `website-${Date.now()}`,
            title: '🌐 Add Business Website',
            description: this.getWebsiteRecommendation(businessType, competitors),
            priority: 'Critical',
            category: 'Digital Presence',
            source: 'profile-strength',
            estimatedTime: '60 minutes'
        };
    }

    /**
     * Remove duplicate tasks based on title similarity
     */
    static deduplicateTasks(tasks) {
        const seen = new Set();
        const uniqueTasks = [];

        for (const task of tasks) {
            // Create a normalized title for comparison (remove emojis, convert to lowercase, trim)
            const normalizedTitle = task.title
                .replace(/[\u{1F600}-\u{1F64F}]|[\u{1F300}-\u{1F5FF}]|[\u{1F680}-\u{1F6FF}]|[\u{1F1E0}-\u{1F1FF}]|[\u{2600}-\u{26FF}]|[\u{2700}-\u{27BF}]/gu, '')
                .toLowerCase()
                .trim();

            // Check for similar titles (website, photos, etc.)
            const isDuplicate = Array.from(seen).some(seenTitle => {
                return seenTitle.includes('website') && normalizedTitle.includes('website') ||
                    seenTitle.includes('photo') && normalizedTitle.includes('photo') ||
                    seenTitle.includes('review') && normalizedTitle.includes('review') ||
                    seenTitle.includes('category') && normalizedTitle.includes('category') ||
                    seenTitle === normalizedTitle;
            });

            if (!isDuplicate) {
                seen.add(normalizedTitle);
                uniqueTasks.push(task);
            } else {
                logger.log(`[Deduplication] Removed duplicate task: ${task.title}`);
            }
        }

        return uniqueTasks;
    }

    /**
     * Comprehensive Business Profile Analysis - AI evaluates every aspect
     */
    static performComprehensiveProfileAnalysis(business, businessType) {
        // Debug logging to see the full business object structure
        logger.log('[AI Analysis] Full business object:', business);
        logger.log('[AI Analysis] Business categories field:', business.categories);
        logger.log('[AI Analysis] Business category field (singular):', business.category);

        // Try to extract categories from different possible fields
        let categories = business.categories;
        if (!categories && business.category) {
            // If categories array doesn't exist but single category does, create array
            categories = [{ displayName: business.category }];
            logger.log('[AI Analysis] Created categories array from single category:', categories);
        }

        const analysis = {
            description: this.analyzeDescription(business.profile?.description, businessType),
            photos: this.analyzePhotos(business.photos, businessType),
            businessHours: this.analyzeBusinessHours(business.regularHours, businessType),
            contactInfo: this.analyzeContactInfo(business, businessType),
            categories: this.analyzeCategories(categories, businessType),
            attributes: this.analyzeAttributes(business.attributes, businessType),
            reviews: this.analyzeReviewManagement(business, businessType),
            completeness: this.analyzeProfileCompleteness(business)
        };

        return analysis;
    }

    /**
     * Analyze business description quality
     */
    static analyzeDescription(description, businessType) {
        if (!description || description.trim().length === 0) {
            return {
                score: 0,
                issues: ['No business description provided'],
                recommendations: [`Add a compelling ${businessType.toLowerCase()} description with services, location, and unique value proposition`],
                priority: 'Critical'
            };
        }

        const issues = [];
        const recommendations = [];
        let score = 50; // Base score for having a description

        // Length analysis
        if (description.length < 100) {
            issues.push('Description too short (under 100 characters)');
            recommendations.push('Expand description to 150-300 characters for better SEO');
        } else if (description.length > 750) {
            issues.push('Description too long (over 750 characters)');
            recommendations.push('Shorten description to under 750 characters');
            score -= 10;
        } else {
            score += 20;
        }

        // Keyword analysis for business type
        const businessKeywords = this.getBusinessTypeKeywords(businessType);
        const hasRelevantKeywords = businessKeywords.some(keyword =>
            description.toLowerCase().includes(keyword.toLowerCase())
        );

        if (!hasRelevantKeywords) {
            issues.push(`Missing ${businessType.toLowerCase()} relevant keywords`);
            recommendations.push(`Include keywords like: ${businessKeywords.slice(0, 3).join(', ')}`);
            score -= 15;
        } else {
            score += 15;
        }

        // Call-to-action analysis
        const hasCallToAction = /contact|call|visit|book|schedule|order|buy/i.test(description);
        if (!hasCallToAction) {
            issues.push('No clear call-to-action');
            recommendations.push('Add a call-to-action like "Call today" or "Visit us"');
            score -= 10;
        } else {
            score += 15;
        }

        return {
            score: Math.max(0, Math.min(100, score)),
            issues,
            recommendations,
            priority: score < 50 ? 'High' : score < 80 ? 'Medium' : 'Low'
        };
    }

    /**
     * Analyze photo quality and quantity
     */
    static analyzePhotos(photos, businessType) {
        const photoCount = photos?.length || 0;
        const issues = [];
        const recommendations = [];
        let score = 0;

        if (photoCount === 0) {
            return {
                score: 0,
                issues: ['No photos uploaded'],
                recommendations: [`Upload ${this.getRecommendedPhotoCount(businessType)} high-quality photos showcasing your ${businessType.toLowerCase()} business`],
                priority: 'Critical'
            };
        }

        // Quantity analysis
        const recommendedCount = this.getRecommendedPhotoCount(businessType);
        if (photoCount < recommendedCount) {
            issues.push(`Only ${photoCount} photos (recommended: ${recommendedCount})`);
            recommendations.push(`Upload ${recommendedCount - photoCount} more photos`);
            score = (photoCount / recommendedCount) * 60;
        } else {
            score = 60;
        }

        // Photo type analysis for business type
        const photoTypeRecommendations = this.getPhotoTypeRecommendations(businessType);
        recommendations.push(...photoTypeRecommendations);

        // Recent photos
        if (photoCount > 0) {
            score += 20; // Bonus for having photos
            recommendations.push('Update photos regularly to keep content fresh');
        }

        return {
            score: Math.max(0, Math.min(100, score)),
            issues,
            recommendations,
            priority: score < 40 ? 'Critical' : score < 70 ? 'High' : 'Medium'
        };
    }

    /**
     * Analyze business hours completeness
     */
    static analyzeBusinessHours(regularHours, businessType) {
        const issues = [];
        const recommendations = [];
        let score = 0;

        if (!regularHours || !regularHours.periods || regularHours.periods.length === 0) {
            return {
                score: 0,
                issues: ['No business hours provided'],
                recommendations: ['Add complete business hours for all operating days'],
                priority: 'High'
            };
        }

        const periods = regularHours.periods;
        const daysWithHours = new Set(periods.map(p => p.openDay));

        // Check if all 7 days are covered
        if (daysWithHours.size < 7) {
            issues.push(`Hours missing for ${7 - daysWithHours.size} days`);
            recommendations.push('Add hours for all days (mark closed days as "Closed")');
            score = (daysWithHours.size / 7) * 80;
        } else {
            score = 80;
        }

        // Business type specific recommendations
        const hourRecommendations = this.getBusinessHoursRecommendations(businessType);
        recommendations.push(...hourRecommendations);

        return {
            score: Math.max(0, Math.min(100, score)),
            issues,
            recommendations,
            priority: score < 60 ? 'High' : 'Medium'
        };
    }

    /**
     * Analyze contact information completeness
     */
    static analyzeContactInfo(business, businessType) {
        const issues = [];
        const recommendations = [];
        let score = 0;

        // Phone number
        if (!business.phone) {
            issues.push('No phone number provided');
            recommendations.push('Add a local phone number for customer contact');
        } else {
            score += 40;
        }

        // Website
        if (!business.websiteUri) {
            issues.push('No website provided');
            recommendations.push(`Add a professional website for your ${businessType.toLowerCase()} business`);
        } else {
            score += 40;
        }

        // Address
        if (!business.address || business.address === 'Address not available') {
            issues.push('No complete address provided');
            recommendations.push('Ensure complete address is visible to customers');
        } else {
            score += 20;
        }

        return {
            score: Math.max(0, Math.min(100, score)),
            issues,
            recommendations,
            priority: score < 60 ? 'Critical' : score < 80 ? 'High' : 'Medium'
        };
    }

    /**
     * Analyze business categories
     */
    static analyzeCategories(categories, businessType) {
        const issues = [];
        const recommendations = [];
        let score = 0;

        // Debug logging to see what categories data we're getting
        logger.log('[AI Analysis] Categories data received:', categories);
        logger.log('[AI Analysis] Categories type:', typeof categories);
        logger.log('[AI Analysis] Categories length:', categories?.length);

        const categoryCount = categories?.length || 0;

        if (categoryCount === 0) {
            return {
                score: 0,
                issues: ['No business categories detected in data'],
                recommendations: [`Add additional categories for ${businessType.toLowerCase()} businesses (primary category may exist but not detected)`],
                priority: 'High'
            };
        }

        if (categoryCount === 1) {
            issues.push('Only primary category exists');
            recommendations.push(`Good! Primary category detected. Add 2-3 additional relevant categories for ${businessType.toLowerCase()} businesses to improve discoverability`);
            score = 60; // Higher score since primary exists
        } else if (categoryCount < 4) {
            score = 70 + (categoryCount - 1) * 10;
            recommendations.push('Consider adding one more relevant category');
        } else {
            score = 100;
        }

        // Business type specific category recommendations
        const categoryRecommendations = this.getCategoryRecommendation(businessType);
        recommendations.push(categoryRecommendations);

        return {
            score: Math.max(0, Math.min(100, score)),
            issues,
            recommendations,
            priority: score < 50 ? 'High' : 'Medium'
        };
    }

    /**
     * Analyze business attributes
     */
    static analyzeAttributes(attributes, businessType) {
        const issues = [];
        const recommendations = [];
        let score = 0;

        const attributeCount = attributes?.length || 0;

        if (attributeCount === 0) {
            issues.push('No business attributes selected');
            recommendations.push('Add relevant business attributes (payment methods, accessibility, etc.)');
            score = 0;
        } else if (attributeCount < 5) {
            issues.push(`Only ${attributeCount} attributes selected`);
            recommendations.push('Add more relevant attributes to help customers understand your services');
            score = attributeCount * 15;
        } else {
            score = 80;
        }

        // Business type specific attribute recommendations
        const attributeRecommendations = this.getAttributeRecommendations(businessType);
        recommendations.push(...attributeRecommendations);

        return {
            score: Math.max(0, Math.min(100, score)),
            issues,
            recommendations,
            priority: score < 40 ? 'Medium' : 'Low'
        };
    }

    /**
     * Analyze review management
     */
    static analyzeReviewManagement(business, businessType) {
        const issues = [];
        const recommendations = [];
        let score = 0;

        const reviewCount = business.reviewCount || 0;
        const rating = business.rating || 0;

        // Review count analysis
        if (reviewCount === 0) {
            issues.push('No customer reviews');
            recommendations.push('Start collecting customer reviews through follow-up emails or in-person requests');
            score = 0;
        } else if (reviewCount < 10) {
            issues.push(`Only ${reviewCount} reviews`);
            recommendations.push('Aim for at least 25+ reviews for better credibility');
            score = reviewCount * 3;
        } else {
            score = 40;
        }

        // Rating analysis
        if (rating < 4.0 && reviewCount > 0) {
            issues.push(`Low rating (${rating}/5.0)`);
            recommendations.push('Focus on improving service quality and addressing customer concerns');
        } else if (rating >= 4.0) {
            score += 30;
        }

        // Response rate (if available)
        const responseRate = business.performance?.responseRate || 0;
        if (responseRate === 0 && reviewCount > 0) {
            issues.push('Not responding to customer reviews');
            recommendations.push('Respond to all reviews within 24-48 hours');
        } else if (responseRate > 0) {
            score += 30;
        }

        return {
            score: Math.max(0, Math.min(100, score)),
            issues,
            recommendations,
            priority: score < 50 ? 'High' : score < 75 ? 'Medium' : 'Low'
        };
    }

    /**
     * Analyze overall profile completeness
     */
    static analyzeProfileCompleteness(business) {
        const fields = {
            name: !!business.name,
            description: !!(business.profile?.description),
            phone: !!business.phone,
            website: !!business.websiteUri,
            address: !!(business.address && business.address !== 'Address not available'),
            hours: !!(business.regularHours?.periods?.length),
            photos: !!(business.photos?.length),
            categories: !!(business.categories?.length)
        };

        const completedFields = Object.values(fields).filter(Boolean).length;
        const totalFields = Object.keys(fields).length;
        const completionPercentage = Math.round((completedFields / totalFields) * 100);

        const missingFields = Object.keys(fields).filter(key => !fields[key]);

        return {
            score: completionPercentage,
            completedFields,
            totalFields,
            missingFields,
            priority: completionPercentage < 60 ? 'Critical' : completionPercentage < 80 ? 'High' : 'Medium'
        };
    }

    /**
     * Generate tasks based on comprehensive profile analysis
     */
    static generateTasksFromProfileAnalysis(analysis, businessType) {
        const tasks = [];

        // Description tasks
        if (analysis.description.score < 80) {
            tasks.push({
                id: `description-improvement-${Date.now()}`,
                title: '📝 Improve Business Description',
                description: `Issues: ${analysis.description.issues.join(', ')}. ${analysis.description.recommendations[0]}`,
                priority: analysis.description.priority,
                category: 'Content Optimization',
                source: 'ai-analysis',
                estimatedTime: '30 minutes',
                aiInsight: 'A compelling description with keywords improves local search visibility'
            });
        }

        // Photo tasks
        if (analysis.photos.score < 70) {
            tasks.push({
                id: `photo-improvement-${Date.now()}`,
                title: '📸 Enhance Photo Strategy',
                description: `${analysis.photos.recommendations[0]}`,
                priority: analysis.photos.priority,
                category: 'Visual Content',
                source: 'ai-analysis',
                estimatedTime: '45 minutes',
                aiInsight: 'High-quality photos increase customer engagement and trust'
            });
        }

        // Business hours tasks
        if (analysis.businessHours.score < 80) {
            tasks.push({
                id: `hours-completion-${Date.now()}`,
                title: '🕒 Complete Business Hours',
                description: `${analysis.businessHours.recommendations[0]}`,
                priority: analysis.businessHours.priority,
                category: 'Profile Completion',
                source: 'ai-analysis',
                estimatedTime: '15 minutes',
                aiInsight: 'Complete hours help customers know when you\'re available'
            });
        }

        // Contact info tasks
        if (analysis.contactInfo.score < 80) {
            tasks.push({
                id: `contact-improvement-${Date.now()}`,
                title: '📞 Complete Contact Information',
                description: `Missing: ${analysis.contactInfo.issues.join(', ')}. ${analysis.contactInfo.recommendations[0]}`,
                priority: analysis.contactInfo.priority,
                category: 'Profile Completion',
                source: 'ai-analysis',
                estimatedTime: '20 minutes',
                aiInsight: 'Complete contact info makes it easy for customers to reach you'
            });
        }

        // Categories tasks
        if (analysis.categories.score < 70) {
            tasks.push({
                id: `categories-expansion-${Date.now()}`,
                title: '🏷️ Expand Business Categories',
                description: `${analysis.categories.recommendations[0]}`,
                priority: analysis.categories.priority,
                category: 'Profile Optimization',
                source: 'ai-analysis',
                estimatedTime: '15 minutes',
                aiInsight: 'More categories help customers find you for different services'
            });
        }

        // Review management tasks
        if (analysis.reviews.score < 70) {
            tasks.push({
                id: `review-management-${Date.now()}`,
                title: '⭐ Improve Review Management',
                description: `${analysis.reviews.recommendations[0]}`,
                priority: analysis.reviews.priority,
                category: 'Reputation Management',
                source: 'ai-analysis',
                estimatedTime: '60 minutes',
                aiInsight: 'Active review management improves ratings and customer trust'
            });
        }

        return tasks;
    }

    // Helper methods for analysis
    static getBusinessTypeKeywords(businessType) {
        const keywords = {
            'Restaurant': ['food', 'dining', 'cuisine', 'menu', 'chef', 'restaurant', 'eat'],
            'Healthcare': ['medical', 'health', 'doctor', 'treatment', 'care', 'clinic', 'patient'],
            'Beauty/Wellness': ['beauty', 'salon', 'spa', 'hair', 'skin', 'wellness', 'relaxation'],
            'Automotive': ['auto', 'car', 'vehicle', 'repair', 'service', 'maintenance', 'mechanic'],
            'Real Estate': ['property', 'home', 'house', 'real estate', 'buy', 'sell', 'agent'],
            'Legal': ['legal', 'law', 'attorney', 'lawyer', 'consultation', 'advice', 'representation'],
            'Retail': ['shop', 'store', 'products', 'buy', 'sale', 'merchandise', 'retail'],
            'Service Business': ['service', 'professional', 'expert', 'quality', 'reliable', 'local']
        };
        return keywords[businessType] || keywords['Service Business'];
    }

    static getRecommendedPhotoCount(businessType) {
        const counts = {
            'Restaurant': 15,
            'Healthcare': 8,
            'Beauty/Wellness': 12,
            'Automotive': 10,
            'Real Estate': 20,
            'Legal': 6,
            'Retail': 15,
            'Service Business': 10
        };
        return counts[businessType] || 10;
    }

    static getPhotoTypeRecommendations(businessType) {
        const recommendations = {
            'Restaurant': ['Upload food photos', 'Show dining area', 'Include exterior shots'],
            'Healthcare': ['Show clean facilities', 'Display equipment', 'Include waiting areas'],
            'Beauty/Wellness': ['Show treatment rooms', 'Display products', 'Include before/after results'],
            'Automotive': ['Show service bays', 'Display equipment', 'Include completed work'],
            'Real Estate': ['Show office space', 'Include team photos', 'Display success stories'],
            'Legal': ['Show professional office', 'Include consultation areas', 'Display credentials'],
            'Retail': ['Show product displays', 'Include store layout', 'Display seasonal items'],
            'Service Business': ['Show work in progress', 'Include team photos', 'Display results']
        };
        return recommendations[businessType] || recommendations['Service Business'];
    }

    static getBusinessHoursRecommendations(businessType) {
        return ['Consider extended hours for better customer access', 'Update holiday hours seasonally'];
    }

    static getAttributeRecommendations(businessType) {
        const recommendations = {
            'Restaurant': ['Add payment methods', 'Include dining options (takeout, delivery)', 'Specify cuisine type'],
            'Healthcare': ['Add accepted insurance', 'Include accessibility features', 'Specify appointment types'],
            'Beauty/Wellness': ['Add service types', 'Include payment options', 'Specify gender preferences'],
            'Automotive': ['Add service types', 'Include payment methods', 'Specify vehicle types'],
            'Real Estate': ['Add property types', 'Include service areas', 'Specify client types'],
            'Legal': ['Add practice areas', 'Include consultation types', 'Specify payment methods'],
            'Retail': ['Add payment methods', 'Include product categories', 'Specify shopping options'],
            'Service Business': ['Add service types', 'Include payment methods', 'Specify service areas']
        };
        return recommendations[businessType] || recommendations['Service Business'];
    }
}

export default ProfileStrengthTaskService;
